import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock, FileCheck, Wallet, MessageCircle, MailCheck, MailWarning, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DOC_TYPES = ["passport", "ktp", "kk", "vaccine", "photo", "buku_nikah"];

function countdown(depIso) {
  if (!depIso) return null;
  const dep = new Date(depIso);
  const diff = dep.getTime() - Date.now();
  const days = Math.floor(diff / 86400000);
  return { days, past: days < 0 };
}

function docProgress(booking) {
  const docs = booking?.documents || {};
  const uploaded = DOC_TYPES.filter((k) => docs[k]?.url).length;
  return { uploaded, total: DOC_TYPES.length, pct: Math.round((uploaded / DOC_TYPES.length) * 100) };
}

function payProgress(booking) {
  const status = booking?.payment_status || "unpaid";
  return {
    unpaid: 0, dp_paid: 20, paid_full: 100,
  }[status] || 0;
}

export default function DashboardSummary({ user, bookings, onResendVerification }) {
  const [resending, setResending] = React.useState(false);
  const nearest = React.useMemo(() => {
    const withDate = bookings.filter((b) => b.departure_date || b.package_departure_date);
    if (!withDate.length) return null;
    return [...withDate].sort((a, b) => {
      const da = new Date(a.departure_date || a.package_departure_date);
      const db = new Date(b.departure_date || b.package_departure_date);
      return da - db;
    })[0];
  }, [bookings]);

  const cd = nearest ? countdown(nearest.departure_date || nearest.package_departure_date) : null;
  const doc = nearest ? docProgress(nearest) : { uploaded: 0, total: DOC_TYPES.length, pct: 0 };
  const pay = nearest ? payProgress(nearest) : 0;

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email verifikasi dikirim ulang. Cek inbox Anda.");
      if (onResendVerification) onResendVerification();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim email verifikasi");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-5 mb-8" data-testid="dashboard-summary">
      {/* Email verification banner */}
      {user && user.email_verified === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="verify-email-banner">
          <div className="flex items-center gap-3">
            <MailWarning className="h-5 w-5 text-amber-700 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Email belum diverifikasi</p>
              <p className="text-xs text-amber-800">Cek email <b>{user.email}</b> atau kirim ulang tautan verifikasi.</p>
            </div>
          </div>
          <button
            onClick={handleResend}
            disabled={resending}
            data-testid="resend-verify-btn"
            className="text-xs px-4 py-2 rounded-full bg-amber-900 text-white font-semibold hover:bg-amber-800 disabled:opacity-60"
          >
            {resending ? "Mengirim..." : "Kirim Ulang Verifikasi"}
          </button>
        </div>
      )}
      {user && user.email_verified === true && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2.5 text-emerald-900 text-xs" data-testid="email-verified-banner">
          <MailCheck className="h-4 w-4" />
          <span>Email <b>{user.email}</b> terverifikasi</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Countdown */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="summary-countdown">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="h-4 w-4 text-accent" />
            <p className="overline !text-[10px]">Keberangkatan Terdekat</p>
          </div>
          {cd ? (
            <>
              <p className="font-serif text-4xl text-primary">
                {cd.past ? "—" : cd.days} <span className="text-lg text-muted-foreground">hari</span>
              </p>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-1">{nearest.package_name}</p>
              <p className="text-[11px] text-accent mt-1 font-mono">
                {new Date(nearest.departure_date || nearest.package_departure_date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </>
          ) : (
            <>
              <p className="font-serif text-2xl text-muted-foreground">Belum ada</p>
              <p className="text-xs text-muted-foreground mt-2">Belum ada booking konfirmasi</p>
            </>
          )}
        </div>

        {/* Doc progress */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="summary-docs">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck className="h-4 w-4 text-accent" />
            <p className="overline !text-[10px]">Kelengkapan Dokumen</p>
          </div>
          <p className="font-serif text-4xl text-primary">
            {doc.uploaded}<span className="text-lg text-muted-foreground">/{doc.total}</span>
          </p>
          <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${doc.pct}%` }} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">{doc.pct}% lengkap</p>
        </div>

        {/* Payment progress */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="summary-payment">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-4 w-4 text-accent" />
            <p className="overline !text-[10px]">Progress Pembayaran</p>
          </div>
          <p className="font-serif text-4xl text-primary">
            {pay}<span className="text-lg text-muted-foreground">%</span>
          </p>
          <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
            <div className={`h-full transition-all ${pay === 100 ? "bg-emerald-500" : pay > 0 ? "bg-amber-500" : "bg-red-400"}`} style={{ width: `${Math.max(pay, 4)}%` }} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            {pay === 100 ? "Lunas" : pay > 0 ? "DP Terbayar" : "Belum ada pembayaran"}
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-card border border-border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Aksi Cepat</p>
        <div className="flex flex-wrap gap-2">
          <Link to="/packages" data-testid="qa-packages" className="text-xs px-4 py-2 rounded-full border border-border hover:bg-muted hover:border-accent transition-colors inline-flex items-center gap-1.5">
            Lihat Paket <ArrowRight className="h-3 w-3" />
          </Link>
          <a href="https://wa.me/6282114294101" target="_blank" rel="noopener noreferrer" data-testid="qa-cs" className="text-xs px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5">
            <MessageCircle className="h-3 w-3" /> Hubungi CS
          </a>
        </div>
      </div>
    </div>
  );
}
